import type { Card } from '@cardengine/shared';
import { useSyncExternalStore } from 'react';

/**
 * The 52 card faces, sliced from Bad Hand Studio's sheet (see
 * `tools/build-deck.mjs`). Each value is a standalone `<svg>` carrying its own
 * card stock, indices and artwork, so the app draws the deck as they drew it.
 *
 * It is roughly 300 KB gzipped — most of it the twelve engraved courts — which
 * is far too much to sit in the main bundle for a visitor who only wanted to
 * read the rules. It loads as its own chunk instead, kicked off at start-up so
 * it is long since ready by the time anyone is dealt a hand.
 */
type DeckArt = Record<string, string>;

let art: DeckArt | null = null;
let pending: Promise<DeckArt> | null = null;
const listeners = new Set<() => void>();

/** Starts the download if it has not started already. Safe to call repeatedly. */
export function loadDeckArt(): Promise<DeckArt> {
  pending ??= import('./deck.json')
    .then((module) => {
      art = module.default;
      for (const listener of listeners) listener();
      return art;
    })
    .catch((error: unknown) => {
      // Let a later attempt retry rather than caching the failure forever.
      pending = null;
      throw error;
    });
  return pending;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The deck once it has arrived, or null while it is still in flight. */
export function useDeckArt(): DeckArt | null {
  return useSyncExternalStore(
    subscribe,
    () => art,
    () => null,
  );
}

export function cardArtKey(card: Card): string {
  return `${card.rank}${card.suit}`;
}
